/**
 * @file GameSkin.ts
 * @role Selects one build-time visual skin and validates its visual-only configuration before
 *       views or assets are used. Gameplay/RGS configuration is intentionally absent here.
 * @owns Skin package selection, cross-config validation, and the active presentation values.
 * @neverDoes Load assets. Decide outcomes, wins, balances, or reel motion.
 */
import uiConfigRaw from '@config/ui.config.json';
import reelPresentationConfigRaw from '@config/reel-presentation.config.json';
import particlesConfigRaw from '@config/particles.config.json';
import audioConfigRaw from '@config/audio.config.json';
import fxConfigRaw from '@config/fx.config.json';
import ambientFxConfigRaw from '@config/ambient-fx.config.json';
import symbolFxConfigRaw from '@config/symbol-fx.config.json';
import presentationSequencesConfigRaw from '@config/presentation-sequences.config.json';
import { GameError } from '@core/errors/GameError';
import { UiConfig } from '@core/config/UiConfig';
import { validateParticlesConfig, type ParticlesConfig } from '@core/particles/ParticleService';
import { validateAudioConfig, type AudioConfig } from '@core/audio/AudioService';
import { validateFxConfig, type FxConfig } from '@core/postfx/PostFxService';
import { validateAmbientFxConfig, type AmbientFxConfig } from '@game/ambient/AmbientFxService';
import { ReelPresentationConfig } from '@game/reels/ReelPresentationConfig';
import {
  collectPresentationSequenceReferences,
  validatePresentationSequencesConfig,
  type PresentationSequencesConfig,
} from '@game/presentation/PresentationSequenceConfig';
import type { SymbolId } from '@game/reels/SymbolId';
import {
  validateSymbolFxConfig,
  type SymbolFxConfig,
  type SymbolFxEntry,
} from '@game/reels/SymbolEffectController';
import type { AudioSourceCatalog, AudioSourceCatalogResult } from '@core/audio/SoundCatalog';
import { LUCKY_LIGHTS_AUDIO_SOURCES } from './GameSkinAudioSources';

export interface GameSkinPackage {
  id: string;
  ui: unknown;
  reels: unknown;
  particles: unknown;
  audio: unknown;
  audioSources: AudioSourceCatalogResult;
  fx: unknown;
  ambient: unknown;
  symbolFx: unknown;
  presentationSequences: unknown;
}

export const DEFAULT_SKIN_ID = 'lucky-lights';

export const GAME_SKIN_PACKAGES: Readonly<Record<string, GameSkinPackage>> = {
  [DEFAULT_SKIN_ID]: {
    id: DEFAULT_SKIN_ID,
    ui: uiConfigRaw,
    reels: reelPresentationConfigRaw,
    particles: particlesConfigRaw,
    audio: audioConfigRaw,
    audioSources: LUCKY_LIGHTS_AUDIO_SOURCES,
    fx: fxConfigRaw,
    ambient: ambientFxConfigRaw,
    symbolFx: symbolFxConfigRaw,
    presentationSequences: presentationSequencesConfigRaw,
  },
};

export class GameSkin {
  readonly ui: UiConfig;
  readonly reels: ReelPresentationConfig;
  readonly particles: ParticlesConfig;
  readonly audio: AudioConfig;
  readonly audioSources: AudioSourceCatalog;
  readonly audioSourceIssues: readonly string[];
  readonly fx: FxConfig;
  readonly ambient: AmbientFxConfig;
  readonly symbolFx: SymbolFxConfig;
  readonly presentationSequences: PresentationSequencesConfig;

  constructor(
    readonly id: string,
    packageConfig: GameSkinPackage,
    symbolIds: readonly SymbolId[],
    knownAssetIds: ReadonlySet<string>,
  ) {
    if (packageConfig.id !== id) {
      throw new GameError('CONFIG_LOAD_FAILED', `Skin package id mismatch: expected ${id}`);
    }
    this.ui = new UiConfig(packageConfig.ui);
    this.reels = new ReelPresentationConfig(symbolIds, packageConfig.reels);
    this.particles = validateParticlesConfig(packageConfig.particles);
    this.audio = validateAudioConfig(packageConfig.audio);
    this.audioSources = packageConfig.audioSources.sources;
    this.audioSourceIssues = packageConfig.audioSources.issues;
    this.fx = validateFxConfig(packageConfig.fx);
    this.ambient = validateAmbientFxConfig(packageConfig.ambient);
    this.symbolFx = validateSymbolFxConfig(packageConfig.symbolFx);
    this.presentationSequences = validatePresentationSequencesConfig(
      packageConfig.presentationSequences,
    );
    this.validateAssetReferences(knownAssetIds);
    this.validateCrossReferences(symbolIds);
  }

  private validateAssetReferences(knownAssetIds: ReadonlySet<string>): void {
    const assetIds = new Set<string>(this.reels.referencedAssetIds());
    const { hudCluster, backgrounds } = this.ui;
    for (const id of [
      hudCluster.roundButtonBackgroundTextureId,
      hudCluster.spinButtonBackgroundTextureId,
      hudCluster.dockBackgroundTextureId,
      hudCluster.portraitDockBackgroundTextureId,
      hudCluster.portraitPanelBackgroundTextureId,
      hudCluster.portraitInfoBackgroundTextureId,
      hudCluster.mobileLandscapePanelBackgroundTextureId,
      hudCluster.infoBackgroundTextureId,
      hudCluster.desktopInfoBackgroundTextureId,
      hudCluster.desktopSpinButtonBackgroundTextureId,
      hudCluster.desktopSpinIconTextureId,
      hudCluster.panelBackgroundTextureId,
      hudCluster.desktopPanelBackgroundTextureId,
      ...Object.values(backgrounds.byLayoutMode).map(({ assetId }) => assetId),
      this.ui.paytableRules.wildCallout.textureId,
      this.ui.paytableRules.scatterCallout.textureId,
      ...this.ui.gameTitle.fontPresets.map((preset) => preset.assetId),
      ...this.ui.backgrounds.neon.placements.map((placement) => placement.textureId),
    ]) {
      if (id) assetIds.add(id);
    }
    // Paytable artwork is no longer named here: the panel draws each row with the SAME symbol
    // art the reels use (reel-presentation.config.json), which this already validates.
    for (const id of this.ambient.textureIds) assetIds.add(id);
    const missing = [...assetIds].filter((id) => !knownAssetIds.has(id));
    if (missing.length > 0) {
      throw new GameError(
        'CONFIG_LOAD_FAILED',
        `Skin "${this.id}" references unknown assets: ${missing.join(', ')}`,
      );
    }
  }

  private validateCrossReferences(symbolIds: readonly SymbolId[]): void {
    // The choreography config names presets that other configs own. Resolving those names here,
    // at the one boot-time cross-config boundary, is what keeps a typo in the matrix a failed
    // boot instead of a cue that silently never fires in front of a player (issue #105).
    const choreography = collectPresentationSequenceReferences(this.presentationSequences);

    const referencedParticlePresets = new Set<string>([
      this.ui.winPresentation.celebrationPreset,
      ...choreography.particlePresets,
    ]);
    const collectSparklePreset = (entry: SymbolFxEntry): void => {
      if (entry.sparkleOverlay) referencedParticlePresets.add(entry.sparkleOverlay.preset);
    };
    for (const entry of Object.values(this.symbolFx.tiers)) collectSparklePreset(entry);
    for (const entry of Object.values(this.symbolFx.symbolOverrides)) collectSparklePreset(entry);

    const { neon } = this.ui.backgrounds;
    this.requireKnown(
      'symbol FX overrides for unknown symbols',
      Object.keys(this.symbolFx.symbolOverrides),
      symbolIds,
    );
    this.requireKnown(
      'unknown particle presets',
      referencedParticlePresets,
      Object.keys(this.particles.presets),
    );
    this.requireKnown(
      'an unknown default glow preset',
      [this.fx.defaultGlowPreset],
      Object.keys(this.fx.glowPresets),
    );
    this.requireKnown(
      'unknown neon sequences',
      choreography.neonSequenceIds,
      neon.sequences.map(({ id }) => id),
    );
    this.requireKnown(
      'unknown neon movement profiles',
      choreography.neonMovementProfiles,
      Object.keys(neon.movementProfiles),
    );
    this.requireKnown(
      'unknown title animation presets',
      choreography.titleAnimationPresets,
      this.ui.gameTitle.animationPresets.map(({ id }) => id),
    );
  }

  /** One shape for every "this config names something another config must define" check. */
  private requireKnown(label: string, referenced: Iterable<string>, known: Iterable<string>): void {
    const defined = new Set<string>(known);
    const unknown = [...referenced].filter((id) => !defined.has(id));
    if (unknown.length > 0) {
      throw new GameError(
        'CONFIG_LOAD_FAILED',
        `Skin "${this.id}" references ${label}: ${unknown.join(', ')}`,
      );
    }
  }
}

/** Build-time skin selection. Unknown values deliberately use the explicit default skin. */
export class SkinRegistry {
  readonly requestedId: string;
  readonly active: GameSkin;

  constructor(
    symbolIds: readonly SymbolId[],
    knownAssetIds: ReadonlySet<string>,
    requestedId: string | undefined = undefined,
    packages: Readonly<Record<string, GameSkinPackage>> = GAME_SKIN_PACKAGES,
  ) {
    this.requestedId = requestedId || DEFAULT_SKIN_ID;
    const packageConfig = packages[this.requestedId] ?? packages[DEFAULT_SKIN_ID];
    if (!packageConfig) {
      throw new GameError('CONFIG_LOAD_FAILED', `Default skin "${DEFAULT_SKIN_ID}" is missing`);
    }
    this.active = new GameSkin(packageConfig.id, packageConfig, symbolIds, knownAssetIds);
  }

  get usedFallback(): boolean {
    return this.active.id !== this.requestedId;
  }
}
